import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import {
  CheckboxGroup,
  ControlGroup,
  Field,
  formConsumer,
  FormGroup,
  InputGroup,
  PasswordInput,
  RadioGroup,
  Select,
  Switch
} from 'core/form';
import { Box, Callout, Flex, FlexColumn, Grid, Heading, Icon, Link, Text, Tooltip } from 'core/components';
import {
  CredentialOptionRenderer,
  CredentialValueRenderer,
  KentikAgentOptionRenderer
} from 'app/views/setup/tasks/nms/optionRenderers';
import SetupCredentials from 'app/views/setup/tasks/nms/SetupCredentials';
import { GROUP_ID_PREFIX, Statuses } from '@kentik/ui-shared/agents/constants';
import MonitoringTemplateSelector from 'app/views/metrics/components/MonitoringTemplateSelect';
import { CREDENTIAL_TYPES } from '@kentik/ui-shared/credentials/constants';
import { isIpValid } from 'core/util/ip';
import { isEmpty } from 'lodash';
import { TestConnection } from 'app/views/settings/devices/form/TestConnection';

const ALLOWED_CREDENTIAL_TYPES = [CREDENTIAL_TYPES.snmp_v1, CREDENTIAL_TYPES.snmp_v2c, CREDENTIAL_TYPES.snmp_v3];

export const collectionMethodRenderer = ({ label, helpText, tooltip }) => (
  <Flex flexDirection="column" lineHeight="20px" mt="-1px">
    <Flex gap={1} alignItems="center">
      <Text ellipsis title={label} fontWeight="medium">
        {label}
      </Text>
      {tooltip && (
        <Tooltip content={tooltip} position="right">
          <Icon icon="info-sign" color="muted" />
        </Tooltip>
      )}
    </Flex>
    {helpText && <Text muted>{helpText}</Text>}
  </Flex>
);

@formConsumer
@inject('$auth', '$devices', '$dictionary', '$discovery', '$credentials', '$kentikAgents', '$setup')
@observer
class IpSnmpSettings extends Component {
  state = {
    isAddingCredentialDialogOpen: false,
    credentialModel: null,
    isTestingConnection: false,
    testConnectionResult: null
  };

  constructor(props) {
    super(props);
    const { form, $auth, $kentikAgents } = this.props;

    // Hide the kentik_agent option if user doesn't have recon.enabled permission
    if (!$auth.hasPermission('recon.enabled', { overrideForSudo: false })) {
      const collectionField = form.getField('snmp_collection_method');
      if (collectionField) {
        collectionField.options = collectionField.options.filter((option) => option.value !== 'kentik_agent');
        form.validate();
      }
    }

    const isUaFlowEnrichmentFFEnabled = $auth.hasPermission('recon.uaFlowEnrichment.enabled', {
      overrideForSudo: false
    });

    // If the UA Flow Enrichment FF is disabled, remove the kentik_agent_flow_only option
    const collectionMethod = form.getValue('snmp_collection_method') ?? [];

    if (!isUaFlowEnrichmentFFEnabled && collectionMethod.includes('kentik_agent_flow_only')) {
      form.setValue('snmp_collection_method', []);
      form.setValue('monitoring_template_id', '');
    }

    // Edge case: if device_snmp_ip is set but kproxy is not selected, add it automatically
    const hasDeviceSnmpIp = !!form.getValue('device_snmp_ip');
    if (hasDeviceSnmpIp && !collectionMethod.includes('kproxy')) {
      const newCollectionMethod = [...collectionMethod, 'kproxy'];
      form.setValue('snmp_collection_method', newCollectionMethod);
    }

    const isMonitoredByRanger =
      collectionMethod.includes('kentik_agent') || collectionMethod.includes('kentik_agent_flow_only');

    // Another edge case: if device has no collection method set, it should not have a monitoring template
    if (!isMonitoredByRanger && form.getValue('monitoring_template_id')) {
      form.setValue('monitoring_template_id', '');
    }

    // clear fields that have invalid values due to the referenced object getting deleted
    const credential_name = form.getValue('kentik_agent_snmp_options.credential_name');
    if (credential_name && !this.credentialOptions.find(({ value }) => value === credential_name)) {
      form.setValue('kentik_agent_snmp_options.credential_name', '');
    }

    const agent_id = form.getValue('kentik_agent_snmp_options.agent_id');
    if (
      agent_id &&
      !agent_id.includes?.(GROUP_ID_PREFIX) &&
      !$kentikAgents.authorizedKentikAgentOptions.find(({ value }) => value === agent_id)
    ) {
      form.setValue('kentik_agent_snmp_options.agent_id', '');
    }
  }

  componentDidUpdate() {
    this.updateRules();
  }

  get credentialOptions() {
    const { $auth, $credentials } = this.props;
    const credentialOptions = $credentials.getCredentialOptionsByTypes(ALLOWED_CREDENTIAL_TYPES);
    if ($auth.hasRbacPermissions(['credential_vault::create'])) {
      credentialOptions.unshift({ value: '$NEW', label: 'New Credential', icon: 'plus', intent: 'primary' });
    }
    return credentialOptions;
  }

  get isAgentNotRunning() {
    const { $kentikAgents, form } = this.props;
    const agent_id = form.getValue('kentik_agent_snmp_options.agent_id');
    return (
      agent_id &&
      !agent_id.includes?.(GROUP_ID_PREFIX) &&
      $kentikAgents.collection.find({ agent_id })?.get('status') !== Statuses.UP
    );
  }

  get canTestConnection() {
    const { form } = this.props;
    const { isTestingConnection } = this.state;

    // Don't allow testing if already in progress
    if (isTestingConnection) {
      return false;
    }

    // Required fields for test connection
    const requiredFields = [
      'kentik_agent_snmp_options.credential_name',
      'kentik_agent_snmp_options.agent_id',
      'kentik_agent_snmp_options.ranger_snmp_ip'
    ];

    // Check if all required fields have values
    const hasRequiredValues = requiredFields.every((fieldName) => {
      const value = form.getValue(fieldName);
      return value && value !== '';
    });

    if (!hasRequiredValues) {
      return false;
    }

    // Validate IP address format
    const ipAddress = form.getValue('kentik_agent_snmp_options.ranger_snmp_ip');
    if (ipAddress && !isIpValid(ipAddress.trim())) {
      return false;
    }

    // Check if form validation is passing for these fields
    const hasValidationErrors = requiredFields.some((fieldName) => {
      const field = form.getField(fieldName);
      return field && field.error;
    });

    return !hasValidationErrors;
  }

  updateRules = () => {
    const { form } = this.props;
    const renderSnmpV3 = form.getValue('device_snmp_v3_conf_enabled');
    const fieldState = form.getField('device_snmp_v3_conf.UserName');
    let rules = '';
    if (renderSnmpV3) {
      rules = 'required';
    }
    form.setRules(fieldState, rules);
  };

  handleCollectionMethodChange = (_, value, prevValue) => {
    const { form, $setup, $auth } = this.props;
    const isUaFlowEnrichmentFFEnabled = $auth.hasPermission('recon.uaFlowEnrichment.enabled', {
      overrideForSudo: false
    });

    isUaFlowEnrichmentFFEnabled ? this.resetSNMPFields(prevValue, value) : this.oldResetSNMPFields(value);

    if (value.includes('kentik_agent') && !form.getValue('plan.id')) {
      // set to metrics plan
      form.setValue('plan.id', $setup.plans.find(({ active, plan_type }) => active && plan_type === 'metrics')?.id);
    }
  };

  // Necessary to prevent invalid template assignments when collection method changes
  // otherwise the logic remains the same as before
  // todo: remove once FF is fully rolled out
  oldResetSNMPFields = (method) => {
    const { form } = this.props;
    const cannotAssignTemplate =
      isEmpty(method) || (!method.includes('kentik_agent_flow_only') && !method.includes('kentik_agent'));

    if (cannotAssignTemplate) {
      form.setValue('monitoring_template_id', null);
    }
  };

  resetSNMPFields = (prevCollectionMethod, newCollectionMethod) => {
    const { form } = this.props;
    const sanitizedNewCollectionMethod = Array.isArray(newCollectionMethod)
      ? newCollectionMethod
      : newCollectionMethod?.split(',');
    const sanitizedPrevCollectionMethod = Array.isArray(prevCollectionMethod)
      ? prevCollectionMethod
      : prevCollectionMethod?.split(',');

    const wasKentikAgentCollectionEnabled = sanitizedPrevCollectionMethod.includes('kentik_agent');
    const wasFlowCollectionViaUAEnabled = sanitizedPrevCollectionMethod.includes('kentik_agent_flow_only');
    const isKentikAgentCollectionEnabled = sanitizedNewCollectionMethod.includes('kentik_agent');
    const isFlowCollectionViaUAEnabled = sanitizedNewCollectionMethod.includes('kentik_agent_flow_only');
    const isMonitoredByUA = isKentikAgentCollectionEnabled || isFlowCollectionViaUAEnabled;
    const isKProxyCollectionEnabled = sanitizedNewCollectionMethod.includes('kproxy');

    // Only retain current monitoring template if the same ua collection type is still enabled
    // otherwise we might allow an invalid template type to be assigned to the device (eg: an NMS template to a Flow-only device)
    // or we might try to assign a template when the device is no longer monitored by UA
    const keepExistingTemplate =
      (wasKentikAgentCollectionEnabled && isKentikAgentCollectionEnabled) ||
      (wasFlowCollectionViaUAEnabled && isFlowCollectionViaUAEnabled);
    if (!keepExistingTemplate) {
      form.setValue('monitoring_template_id', null);
    }

    // Clear out kentik_agent fields if kentik_agent collection is disabled
    if (!isMonitoredByUA) {
      form.setValue('kentik_agent_snmp_options.credential_name', '');
      form.setValue('kentik_agent_snmp_options.agent_id', '');
      form.setValue('kentik_agent_snmp_options.ranger_snmp_ip', '');
      form.setValue('kentik_agent_snmp_options.port', '');
      form.setValue('kentik_agent_snmp_options.ranger_icmp_ip', '');
    }

    // Clear out kproxy fields if kproxy collection is disabled
    if (!isKProxyCollectionEnabled) {
      form.setValue('minimize_snmp', false);
      form.setValue('device_snmp_ip', '');
      form.setValue('device_snmp_community', '');
      form.setValue('device_snmp_v3_conf_enabled', false);
    }
  };

  handleCredentialChange = (field, value) => {
    const { $credentials } = this.props;
    // open the new credential dialog
    if (value === '$NEW') {
      field.setValue('');
      this.setState({
        isAddingCredentialDialogOpen: true,
        credentialModel: $credentials.groupedCollection.forge()
      });
    }
  };

  handleCloseCredentialsDialog = (formValues) => {
    const { form } = this.props;

    if (formValues) {
      const { credentialName } = formValues;
      form.setValue('kentik_agent_snmp_options.credential_name', credentialName);
    }

    this.setState({ isAddingCredentialDialogOpen: false, credentialModel: null });
  };

  handleTestConnection = async () => {
    const { form, $discovery } = this.props;
    this.setState({ isTestingConnection: true, testConnectionResult: null });

    const formValues = form.getValues();
    const payload = {
      agentId: formValues.kentik_agent_snmp_options.agent_id,
      credentialName: formValues.kentik_agent_snmp_options.credential_name,
      rangerSnmpIp: formValues.kentik_agent_snmp_options.ranger_snmp_ip,
      port: formValues.kentik_agent_snmp_options.port || '161'
    };

    $discovery
      .rangerTestConnection(payload)
      .then((response) => {
        const isSuccess = response && response.success !== false && !response.error;

        this.setState({
          testConnectionResult: {
            success: isSuccess,
            data: response,
            error: isSuccess ? null : response.error || response.message || 'Connection failed',
            connectionSuccessful: true,
            timestamp: Date.now(),
            parameters: payload
          }
        });
      })
      .catch((error) => {
        this.setState({
          testConnectionResult: {
            success: false,
            error: error.message || 'Request failed',
            connectionSuccessful: false,
            timestamp: Date.now(),
            parameters: payload,
            showParameters: false
          }
        });
      })
      .finally(() => {
        this.setState({ isTestingConnection: false });
      });
  };

  render() {
    const { $auth, $devices, $dictionary, $kentikAgents, form, role } = this.props;
    const { isAddingCredentialDialogOpen, credentialModel, isTestingConnection, testConnectionResult } = this.state;
    const { model } = form;
    const { device_snmp_v3_conf_enabled, device_subtype, snmp_collection_method, device_snmp_v3_conf } =
      form.getValues();
    const { snmpPollingIps } = $devices.routerSettings;
    const kbLink = $dictionary.getHelpUrl('admin', 'configuringSNMP');
    const deviceType = $devices.getDeviceTypeFromSubtype(device_subtype);

    const iskProxyCollectionEnabled = snmp_collection_method?.includes('kproxy');
    const isFlowCollectionViaUAEnabled = snmp_collection_method?.includes('kentik_agent_flow_only');
    const isKentikAgentCollectionEnabled = snmp_collection_method?.includes('kentik_agent');
    const isDoublePollingEnabled = iskProxyCollectionEnabled && isKentikAgentCollectionEnabled;

    const isUaFlowEnrichmentFFEnabled = $auth.hasPermission('recon.uaFlowEnrichment.enabled', {
      overrideForSudo: false
    });
    const ariaProps = {};
    if (role) {
      ariaProps.role = role;
    }

    const collectionMethodOptions = form.getField('snmp_collection_method')?.options || [];

    return (
      <Flex flexDirection="column" gap={2} overflow="auto" p={2} {...ariaProps}>
        <Heading level={5} pb="6px" mb={0} borderBottom="thin">
          Collection Method
        </Heading>

        {/* Feature flag enabled: Use new RadioGroup workflow with collection types */}
        {isUaFlowEnrichmentFFEnabled ? (
          <Field
            onChange={this.handleCollectionMethodChange}
            name="snmp_collection_method"
            showLabel={false}
            m={0}
            options={collectionMethodOptions}
            validateOptions={false}
          >
            <RadioGroup
              containerProps={{ flexDirection: 'column', gap: '8px' }}
              itemRenderer={collectionMethodRenderer}
            />
          </Field>
        ) : (
          /* Feature flag disabled: Use original CheckboxGroup workflow */
          <Field name="snmp_collection_method" onChange={this.handleCollectionMethodChange} showLabel={false} m={0}>
            <CheckboxGroup
              containerProps={{ flexDirection: 'column', gap: '8px' }}
              itemRenderer={collectionMethodRenderer}
            />
          </Field>
        )}

        {isUaFlowEnrichmentFFEnabled && isDoublePollingEnabled && (
          <Callout intent="warning" p={2}>
            <FlexColumn mb="4px">
              <Text>
                <strong>Warning:</strong> This device is configured for SNMP polling via both Kentik Agent and kProxy,
                which will result in duplicate polling that may cause performance issues on your device.
              </Text>
              <Text mt={2}>
                We recommend using only one polling method. Since we have consolidated SNMP polling for both NMS and
                Flow Enrichment, consider switching to <strong>Full monitoring via Universal Agent</strong> exclusively.
                The dual-polling option is now considered deprecated.
              </Text>
            </FlexColumn>
          </Callout>
        )}

        {isKentikAgentCollectionEnabled && (
          <>
            <Heading level={5} pb="6px" mb={0} borderBottom="thin">
              {isUaFlowEnrichmentFFEnabled ? 'For Full Monitoring via Universal Agent' : 'For NMS via Kentik Agent'}
            </Heading>

            {this.isAgentNotRunning && (
              <Callout intent="danger">
                <Flex gap={1} alignItems="center">
                  <Icon icon="error" color="danger" />
                  <Text>This agent isn’t currently running, and won’t be able to receive updates.</Text>
                </Flex>
              </Callout>
            )}

            <Flex gap={2}>
              <Flex flexDirection="column" gap={1} flex={1}>
                <Field
                  disabled={!$auth.hasRbacPermissions(['credential_vault::read'])}
                  m={0}
                  flex={1}
                  onChange={this.handleCredentialChange}
                  name="kentik_agent_snmp_options.credential_name"
                  options={this.credentialOptions}
                >
                  <Select
                    fill
                    optionRenderer={CredentialOptionRenderer}
                    valueRenderer={CredentialValueRenderer}
                    showFilter
                  />
                </Field>
              </Flex>

              <Flex flexDirection="column" gap={1} flex={1}>
                <Field
                  m={0}
                  flex={1}
                  name="kentik_agent_snmp_options.agent_id"
                  options={$kentikAgents.nmsAssignmentOptions}
                  disabled={false}
                >
                  <Select fill showFilter optionRenderer={KentikAgentOptionRenderer} />
                </Field>
              </Flex>

              <Flex flex={1}>
                <Field m={0} name="monitoring_template_id">
                  <MonitoringTemplateSelector />
                </Field>
              </Flex>
            </Flex>

            <Flex gap={2}>
              <Field m={0} name="kentik_agent_snmp_options.ranger_snmp_ip" flex={1}>
                <InputGroup fill />
              </Field>

              <Field m={0} name="kentik_agent_snmp_options.port" placeholder="161" flex={1}>
                <InputGroup fill />
              </Field>
            </Flex>

            <TestConnection
              canTestConnection={this.canTestConnection}
              handleTestConnection={this.handleTestConnection}
              isTestingConnection={isTestingConnection}
              testConnectionResult={testConnectionResult}
            />
          </>
        )}

        {isFlowCollectionViaUAEnabled && isUaFlowEnrichmentFFEnabled && (
          <>
            <Heading level={5} pb="6px" mb={0} borderBottom="thin">
              For Flow Enrichment via Universal Agent
            </Heading>

            {this.isAgentNotRunning && (
              <Callout intent="danger">
                <Flex gap={1} alignItems="center">
                  <Icon icon="error" color="danger" />
                  <Text>This agent isn’t currently running, and won’t be able to receive updates.</Text>
                </Flex>
              </Callout>
            )}

            <Flex gap={2}>
              <Flex flexDirection="column" gap={1} flex={1}>
                <Field
                  disabled={!$auth.hasRbacPermissions(['credential_vault::read'])}
                  m={0}
                  flex={1}
                  onChange={this.handleCredentialChange}
                  name="kentik_agent_snmp_options.credential_name"
                  options={this.credentialOptions}
                >
                  <Select
                    fill
                    optionRenderer={CredentialOptionRenderer}
                    valueRenderer={CredentialValueRenderer}
                    showFilter
                  />
                </Field>
              </Flex>

              <Flex flexDirection="column" gap={1} flex={1}>
                <Field
                  m={0}
                  flex={1}
                  name="kentik_agent_snmp_options.agent_id"
                  options={$kentikAgents.authorizedKentikAgentOptions}
                  disabled={false}
                >
                  <Select fill optionRenderer={KentikAgentOptionRenderer} showFilter />
                </Field>
              </Flex>

              <Flex flex={1}>
                <Field m={0} name="monitoring_template_id">
                  <MonitoringTemplateSelector templateType="FLOW" />
                </Field>
              </Flex>
            </Flex>

            <Flex gap={2}>
              <Field m={0} name="kentik_agent_snmp_options.ranger_snmp_ip" flex={1}>
                <InputGroup fill />
              </Field>

              <Field m={0} name="kentik_agent_snmp_options.port" placeholder="161" flex={1}>
                <InputGroup fill />
              </Field>
            </Flex>

            <TestConnection
              canTestConnection={this.canTestConnection}
              handleTestConnection={this.handleTestConnection}
              isTestingConnection={isTestingConnection}
              testConnectionResult={testConnectionResult}
            />
          </>
        )}
        {iskProxyCollectionEnabled && (
          <>
            <Heading level={5} pb="6px" mb={0} borderBottom="thin">
              For Flow Enrichment via kProxy
            </Heading>

            <Callout intent="warning" p={2}>
              <Flex flexDirection="column" gap="12px">
                <Text>
                  Allow us to SNMP poll your router from these IPs using the Community supplied in the device
                  configuration.
                </Text>
                <Flex ml={2}>
                  <Box width={220}>Kentik SNMP polling IPs:</Box>
                  <Text as="div" monospace fontWeight="bold">
                    {snmpPollingIps}
                  </Text>
                </Flex>
                <Link to={kbLink} blank>
                  Need help configuring SNMP polling?
                </Link>
              </Flex>
            </Callout>

            <Field
              m={0}
              name="minimize_snmp"
              inputStyle={{ width: 160 }}
              rules={deviceType === 'router' ? 'required' : []}
            >
              <Select />
            </Field>

            <Flex gap={2}>
              <Field m={0} name="device_snmp_ip" flex={1}>
                <InputGroup fill />
              </Field>

              {!device_snmp_v3_conf_enabled && (
                <Field m={0} name="device_snmp_community" flex={1}>
                  <PasswordInput fill autoComplete="new-password" />
                </Field>
              )}
            </Flex>

            <Flex flexDirection="column" gap={2}>
              <Field m={0} name="device_snmp_v3_conf_enabled" showLabel={false} onChange={this.updateRules}>
                <Switch switchLabel="Enable SNMP v3 Authentication" />
              </Field>

              {device_snmp_v3_conf_enabled && (
                <Flex flexDirection="column" gap={2}>
                  <Field m={0} name="device_snmp_v3_conf.UserName">
                    <InputGroup width={400} />
                  </Field>

                  <Grid>
                    <FormGroup label="Authentication" fontSize="small" mb={0}>
                      <ControlGroup>
                        <Field
                          large
                          name="device_snmp_v3_conf.AuthenticationProtocol"
                          showLabel={false}
                          onChange={this.updateRules}
                        >
                          <Select menuWidth={100} />
                        </Field>
                        {!(device_snmp_v3_conf.AuthenticationProtocol === 'NoAuth') && (
                          <Field
                            large
                            name="device_snmp_v3_conf.AuthenticationPassphrase"
                            showLabel={false}
                            placeholder="Passphrase"
                            flex={1}
                          >
                            <PasswordInput leftIcon="lock" />
                          </Field>
                        )}
                      </ControlGroup>
                    </FormGroup>

                    <FormGroup label="Privacy" mb={0}>
                      <ControlGroup>
                        <Field
                          large
                          name="device_snmp_v3_conf.PrivacyProtocol"
                          showLabel={false}
                          onChange={this.updateRules}
                        >
                          <Select menuWidth={100} />
                        </Field>
                        {!(device_snmp_v3_conf.PrivacyProtocol === 'NoPriv') && (
                          <Field
                            large
                            name="device_snmp_v3_conf.PrivacyPassphrase"
                            showLabel={false}
                            placeholder="Passphrase"
                            flex={1}
                          >
                            <PasswordInput leftIcon="lock" />
                          </Field>
                        )}
                      </ControlGroup>
                    </FormGroup>
                  </Grid>
                </Flex>
              )}
            </Flex>
          </>
        )}
        {model.isICMP && (
          <Flex flexDirection="column" gap={2}>
            <Heading level={5} pb="6px" mb={0} borderBottom="thin">
              ICMP
            </Heading>
            <Flex gap={2}>
              <Flex flexDirection="column" gap={1} flex={1}>
                <Field name="kentik_agent_snmp_options.ranger_icmp_ip">
                  <InputGroup />
                </Field>
              </Flex>

              <Flex flexDirection="column" gap={1} flex={1}>
                <Field
                  m={0}
                  flex={1}
                  name="kentik_agent_snmp_options.agent_id"
                  options={$kentikAgents.authorizedKentikAgentOptions}
                  disabled={false}
                >
                  <Select fill optionRenderer={KentikAgentOptionRenderer} />
                </Field>
              </Flex>
            </Flex>
          </Flex>
        )}
        {credentialModel && (
          <SetupCredentials
            isOpen={isAddingCredentialDialogOpen}
            model={credentialModel}
            onClose={this.handleCloseCredentialsDialog}
            // only SNMP credential types are valid here
            types={ALLOWED_CREDENTIAL_TYPES}
            title="Add SNMP Credential"
          />
        )}
      </Flex>
    );
  }
}

export default IpSnmpSettings;
